import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ITEM_TYPES } from 'constants/itemType';
import { useApproveRequest, useRejectRequest } from 'hooks/useApprovals';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import ActionDrawer from 'components/IAP/ActionDrawer';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { useIncidentActions } from 'components/IAP/hooks/incidentActionHooks';
import { useIncidentResourceRequests } from 'components/IAP/hooks/incidentResourceRequestHooks';
import { useWorkAssignmentListAttachments } from 'components/IAP/hooks/assignmentListHooks';
import { Ics213RrExpandedPanel } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrExpandedPanel';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { selectUserGuid } from 'slices/commonSelectors';
import ReportsSitrepCard from 'components/Reports/ReportsSitrepCard';
import { SitrepCardSurface } from 'components/Reports/reports.types';
import { useSitrepsPendingReview } from 'components/DisasterGPT/hooks/useSitrepsV2';
import Ics204ReviewDialog from 'components/ReviewQueue/Ics204ReviewDialog';
import type { ReviewQueueItem } from 'components/ReviewQueue/reviewQueueTypes';
import { REVIEW_QUEUE_KIND_LABELS } from 'components/ReviewQueue/reviewQueueTypes';

function LoadingNote() {
  return <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">Loading…</p>;
}

function MissingNote() {
  return (
    <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
      This item is no longer waiting on you.
    </p>
  );
}

function TaskReview({
  item,
  onClose,
}: {
  item: ReviewQueueItem;
  onClose: () => void;
}) {
  const { data: actions = [], isLoading } = useIncidentActions(
    INCIDENT,
    item.incidentId
  );
  const action = actions.find((row) => row.action_id === item.id);
  if (isLoading) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{item.title}</DialogTitle>
          </DialogHeader>
          <LoadingNote />
        </DialogContent>
      </Dialog>
    );
  }
  if (!action) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{item.title}</DialogTitle>
          </DialogHeader>
          <MissingNote />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <ActionDrawer
      scope={INCIDENT}
      selectedEntity={action}
      incidentIdOverride={item.incidentId}
      toggle={onClose}
    />
  );
}

function ReportReview({ item }: { item: ReviewQueueItem }) {
  const pending = useSitrepsPendingReview();
  const sitrep = pending.data?.find(
    (row) => row.id === item.id || row.item_id === item.itemId
  );
  if (pending.isLoading) return <LoadingNote />;
  if (!sitrep) return <MissingNote />;
  return (
    <ReportsSitrepCard sitrep={sitrep} surface={SitrepCardSurface.Review} />
  );
}

function LayerReview({ item }: { item: ReviewQueueItem }) {
  const routeGroupId = useGroupGuid();
  const groupId = item.groupId || routeGroupId || '';
  const approveRequest = useApproveRequest();
  const rejectRequest = useRejectRequest();
  const [decision, setDecision] = useState<'approve' | 'deny' | null>(null);
  const [comments, setComments] = useState('');
  const busy = approveRequest.isPending || rejectRequest.isPending;

  return (
    <form
      className="tw:flex tw:flex-col tw:gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!decision || !item.itemId || !groupId) return;
        const payload = {
          itemType: ITEM_TYPES.InternalLayerVersion,
          itemId: item.itemId,
          groupId,
          incidentId: item.incidentId ?? undefined,
          comments,
        };
        if (decision === 'approve') {
          void approveRequest.mutateAsync(payload);
          return;
        }
        void rejectRequest.mutateAsync(payload);
      }}
    >
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        Approve publishes this layer version. Deny sends it back.
      </p>
      <div className="tw:flex tw:gap-2">
        <Button
          type="button"
          variant={decision === 'approve' ? 'default' : 'outline'}
          onClick={() => setDecision('approve')}
        >
          Approve
        </Button>
        <Button
          type="button"
          variant={decision === 'deny' ? 'default' : 'outline'}
          onClick={() => setDecision('deny')}
        >
          Deny
        </Button>
      </div>
      <Textarea
        value={comments}
        onChange={(event) => setComments(event.target.value)}
        placeholder="Comments"
        rows={3}
      />
      <Button type="submit" disabled={!decision || busy || !item.itemId || !groupId}>
        Submit decision
      </Button>
    </form>
  );
}

function ResourceRequestReview({ item }: { item: ReviewQueueItem }) {
  const userGuid = useSelector(selectUserGuid);
  const { data: roster } = useGetRosterForIncident();
  const { data: requests, isLoading } = useIncidentResourceRequests();
  const memberPositions =
    roster?.find((member) => member.userId === userGuid)?.incidentPositions ||
    [];
  const request = requests?.find(
    (row) => row.resource_request_id === item.id || row.id === item.id
  );
  const reqCount = useMemo(() => {
    const counts: Record<string, number> = {};
    if (!request || request.status === 'completed') return counts;
    request.logistics_items?.flat().forEach((line) => {
      if (line.resource_set_id) {
        counts[line.resource_set_id] = (counts[line.resource_set_id] || 0) + 1;
      }
    });
    return counts;
  }, [request]);
  if (isLoading) return <LoadingNote />;
  if (!request) return <MissingNote />;
  return (
    <Ics213RrExpandedPanel
      request={request}
      memberPositions={memberPositions}
      reqCount={reqCount}
    />
  );
}

function Ics204Review({ item }: { item: ReviewQueueItem }) {
  const { data: attachments, isLoading } = useWorkAssignmentListAttachments();
  const attachment = attachments?.find((row) => row.walaId === item.id);
  if (isLoading) return <LoadingNote />;
  if (!attachment) return <MissingNote />;
  return <Ics204ReviewDialog attachment={attachment} />;
}

export function ReviewQueueDialog({
  item,
  onClose,
}: {
  item: ReviewQueueItem;
  onClose: () => void;
}) {
  if (item.kind === 'task') {
    return <TaskReview item={item} onClose={onClose} />;
  }
  if (item.kind === 'ics204') {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="tw:sm:max-w-5xl tw:max-h-[85vh] tw:overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {REVIEW_QUEUE_KIND_LABELS.ics204}: {item.title}
            </DialogTitle>
          </DialogHeader>
          <Ics204Review item={item} />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="tw:sm:max-w-5xl tw:max-h-[85vh] tw:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {REVIEW_QUEUE_KIND_LABELS[item.kind]}: {item.title}
          </DialogTitle>
        </DialogHeader>
        {item.kind === 'sitrep' ? <ReportReview item={item} /> : null}
        {item.kind === 'layer' ? <LayerReview item={item} /> : null}
        {item.kind === 'resource_request' ? (
          <ResourceRequestReview item={item} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
